/**
 * useCurrentUser Hook
 *
 * Fetches the authenticated user's profile from GET /auth/me, where roles are
 * hydrated from the local DB (User.role). This is the authoritative role source —
 * client-side email comparisons are not (the backend deliberately removed the
 * email-based super-admin backdoor; see backend/src/middleware/auth.js).
 */

import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

function useCurrentUser() {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await axios.get('/api/auth/me', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled) setMe(res.data.user);
            } catch (err) {
                console.error('Error fetching current user:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [isAuthenticated, getAccessTokenSilently]);

    return {
        me,
        loading,
        isSuperAdmin: me?.roles?.includes('super_admin') ?? false,
    };
}

export default useCurrentUser;
